import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import {
  deleteBanner,
  listBanners,
  setBannerActive,
  upsertBanner,
  type BannerRecord,
} from '../../services/banners';
import {
  buildBannerSvgDataUrl,
  DEFAULT_BANNER_BACKGROUND_COLOR,
  DEFAULT_BANNER_TEXT_COLOR,
} from './bannerDesigner';

function isValidBannerImageUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'data:';
  } catch {
    return false;
  }
}

const bannerSchema = z
  .object({
    title: z.string().trim().min(3, 'Başlık zorunludur'),
    description: z.string().optional(),
    sourceMode: z.enum(['image', 'designer']).default('image'),
    imageUrl: z.string().optional().or(z.literal('')),
    designEmoji: z.string().max(8, 'Emoji alanı çok uzun').optional().or(z.literal('')),
    designBackgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Geçerli bir arka plan rengi seçin')
      .default(DEFAULT_BANNER_BACKGROUND_COLOR),
    designTextColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Geçerli bir metin rengi seçin')
      .default(DEFAULT_BANNER_TEXT_COLOR),
    targetUrl: z.string().url('Geçerli bir URL girin').optional().or(z.literal('')),
    orderPriority: z.coerce.number().default(0),
    validFrom: z.string().optional().or(z.literal('')),
    validUntil: z.string().optional().or(z.literal('')),
    isActive: z.boolean().default(true),
  })
  .superRefine((values, ctx) => {
    if (values.sourceMode !== 'image') {
      return;
    }

    const imageUrl = values.imageUrl?.trim() ?? '';

    if (!imageUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Görsel URL zorunludur',
        path: ['imageUrl'],
      });
      return;
    }

    if (!isValidBannerImageUrl(imageUrl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Geçerli bir URL girin',
        path: ['imageUrl'],
      });
    }
  });

type BannerFormValues = z.infer<typeof bannerSchema>;

const DEFAULT_VALUES: BannerFormValues = {
  title: '',
  description: '',
  sourceMode: 'image',
  imageUrl: '',
  designEmoji: '',
  designBackgroundColor: DEFAULT_BANNER_BACKGROUND_COLOR,
  designTextColor: DEFAULT_BANNER_TEXT_COLOR,
  targetUrl: '',
  orderPriority: 0,
  validFrom: '',
  validUntil: '',
  isActive: true,
};

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });
}

function isDesignerBanner(banner: Pick<BannerRecord, 'designEmoji' | 'designBackgroundColor' | 'designTextColor'>) {
  return Boolean(banner.designBackgroundColor || banner.designTextColor || banner.designEmoji);
}

export default function BannerManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerRecord | null>(null);

  const {
    data: banners = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['marketing', 'banners'],
    queryFn: listBanners,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BannerFormValues>({
    resolver: zodResolver(bannerSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const sourceMode = watch('sourceMode');
  const imageUrlValue = watch('imageUrl');
  const titleValue = watch('title');
  const descriptionValue = watch('description');
  const designEmojiValue = watch('designEmoji');
  const designBackgroundColorValue = watch('designBackgroundColor');
  const designTextColorValue = watch('designTextColor');

  const generatedDesignerImageUrl = buildBannerSvgDataUrl({
    title: titleValue,
    description: descriptionValue,
    emoji: designEmojiValue,
    backgroundColor: designBackgroundColorValue,
    textColor: designTextColorValue,
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingBanner(null);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const openCreate = () => {
    setEditingBanner(null);
    reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (banner: BannerRecord) => {
    const usingDesigner = isDesignerBanner(banner);

    setEditingBanner(banner);
    reset({
      title: banner.title,
      description: banner.description ?? '',
      sourceMode: usingDesigner ? 'designer' : 'image',
      imageUrl: usingDesigner ? '' : banner.imageUrl,
      designEmoji: banner.designEmoji ?? '',
      designBackgroundColor: banner.designBackgroundColor ?? DEFAULT_BANNER_BACKGROUND_COLOR,
      designTextColor: banner.designTextColor ?? DEFAULT_BANNER_TEXT_COLOR,
      targetUrl: banner.targetUrl ?? '',
      orderPriority: banner.orderPriority,
      validFrom: banner.validFrom?.slice(0, 10) ?? '',
      validUntil: banner.validUntil?.slice(0, 10) ?? '',
      isActive: banner.isActive,
    });
    setModalOpen(true);
  };

  const upsertMutation = useMutation({
    mutationFn: upsertBanner,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'banners'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (banner: BannerRecord) => deleteBanner(banner.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'banners'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (banner: BannerRecord) => setBannerActive(banner.id, !banner.isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['marketing', 'banners'] });
    },
  });

  const onSubmit = (values: BannerFormValues) => {
    const title = values.title.trim();
    const description = values.description?.trim() || null;
    const usingDesigner = values.sourceMode === 'designer';

    const payload = {
      id: editingBanner?.id,
      title,
      description,
      imageUrl: usingDesigner ? generatedDesignerImageUrl : values.imageUrl?.trim() ?? '',
      designEmoji: usingDesigner ? values.designEmoji?.trim() || null : null,
      designBackgroundColor: usingDesigner ? values.designBackgroundColor : null,
      designTextColor: usingDesigner ? values.designTextColor : null,
      targetUrl: values.targetUrl?.trim() || null,
      orderPriority: Number(values.orderPriority),
      validFrom: values.validFrom ? new Date(values.validFrom).toISOString() : null,
      validUntil: values.validUntil ? new Date(values.validUntil).toISOString() : null,
      isActive: values.isActive,
    };

    return upsertMutation.mutateAsync(payload);
  };

  const switchSourceMode = (mode: BannerFormValues['sourceMode']) => {
    setValue('sourceMode', mode, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (mode === 'image' && (imageUrlValue ?? '').startsWith('data:image/svg+xml')) {
      setValue('imageUrl', '', { shouldDirty: true });
    }
  };

  const activeBannersCount = banners.filter((banner) => banner.isActive).length;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Reklam Bannerları</p>
            <p className="card-subtitle">
              {isLoading || isFetching
                ? 'Banner verileri senkronize ediliyor...'
                : `Toplam ${banners.length} banner, ${activeBannersCount} aktif`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Banner Ekle
          </button>
        </div>

        {banners.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz banner yok</strong>
            <p>Mobil uygulamada görüntülenecek reklam bannerları ekleyin.</p>
          </div>
        ) : (
          <div className="banner-list">
            {banners.map((banner) => {
              const usingDesigner = isDesignerBanner(banner);

              return (
                <div key={banner.id} className="banner-item">
                  <div className="banner-preview">
                    {usingDesigner ? (
                      <div
                        className="ad-preview-card ad-preview-mini-list"
                        style={{
                          backgroundColor: banner.designBackgroundColor || DEFAULT_BANNER_BACKGROUND_COLOR,
                          color: banner.designTextColor || DEFAULT_BANNER_TEXT_COLOR,
                        }}
                      >
                        <div className="ad-preview-content">
                          <div className="ad-preview-title">
                            {banner.title}
                          </div>
                          {banner.description && (
                            <div className="ad-preview-subtitle">
                              {banner.description}
                            </div>
                          )}
                        </div>
                        <span className="ad-preview-emoji">
                          {banner.designEmoji || '📢'}
                        </span>
                      </div>
                    ) : (
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                    )}
                  </div>
                  <div className="banner-info">
                    <div className="banner-header">
                      <strong>{banner.title}</strong>
                      <div className="chip-row">
                        <span className={`chip ${banner.isActive ? 'success' : 'warning'}`}>
                          {banner.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        <span className="chip neutral">
                          {usingDesigner ? 'Tasarım bannerı' : 'Görsel URL'}
                        </span>
                      </div>
                    </div>
                    {banner.description ? <p className="helper-text">{banner.description}</p> : null}
                    <div className="helper-text">
                      <div>Sıra: {banner.orderPriority}</div>
                      <div>
                        Geçerlilik: {formatDate(banner.validFrom)} - {formatDate(banner.validUntil)}
                      </div>
                      {usingDesigner && banner.designEmoji ? (
                        <div>Tasarım emojisi: {banner.designEmoji}</div>
                      ) : null}
                    </div>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="button secondary"
                        onClick={() => openEdit(banner)}
                      >
                        Düzenle
                      </button>
                      <button
                        type="button"
                        className="button ghost"
                        onClick={() => toggleActiveMutation.mutate(banner)}
                      >
                        {banner.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                      </button>
                      <button
                        type="button"
                        className="button danger"
                        onClick={() => {
                          if (
                            window.confirm(
                              `"${banner.title}" bannerını silmek istediğinize emin misiniz?`
                            )
                          ) {
                            deleteMutation.mutate(banner);
                          }
                        }}
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingBanner ? `"${editingBanner.title}" bannerını güncelle` : 'Yeni banner ekle'}
        wide
      >
        <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
          <label className="form-field">
            <span>Başlık</span>
            <input type="text" placeholder="Yılbaşı Kampanyası" {...register('title')} />
            {errors.title ? <span className="error-text">{errors.title.message}</span> : null}
          </label>

          <label className="form-field">
            <span>Açıklama / alt başlık (opsiyonel)</span>
            <textarea
              placeholder="Banner üzerinde gösterilecek kısa açıklama"
              {...register('description')}
            />
          </label>

          <div className="form-field">
            <span>Banner tipi</span>
            <div className="banner-mode-toggle">
              <button
                type="button"
                className={`banner-mode-button ${sourceMode === 'image' ? 'active' : ''}`}
                onClick={() => switchSourceMode('image')}
              >
                Hazır görsel
              </button>
              <button
                type="button"
                className={`banner-mode-button ${sourceMode === 'designer' ? 'active' : ''}`}
                onClick={() => switchSourceMode('designer')}
              >
                Banner tasarla
              </button>
            </div>
            <span className="helper-text">
              Hazır görsel akışını korur. Tasarım modu ise mevcut banner alanına otomatik görsel
              üretir.
            </span>
          </div>

          {sourceMode === 'image' ? (
            <>
              <label className="form-field">
                <span>Görsel URL</span>
                <input
                  type="url"
                  placeholder="https://example.com/banner.jpg"
                  {...register('imageUrl')}
                />
                {errors.imageUrl ? (
                  <span className="error-text">{errors.imageUrl.message}</span>
                ) : null}
                <span className="helper-text">
                  Supabase Storage veya harici bir kaynaktan görsel URL&apos;i yapıştırın.
                </span>
              </label>

              {imageUrlValue ? (
                <div className="form-field">
                  <span>Önizleme</span>
                  <img
                    src={imageUrlValue}
                    alt="Banner önizleme"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                    }}
                    onError={(event) => {
                      (event.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="designer-grid">
                <label className="form-field">
                  <span>Emoji</span>
                  <input type="text" maxLength={8} placeholder="🍕" {...register('designEmoji')} />
                </label>

                <label className="form-field">
                  <span>Arka plan rengi</span>
                  <input type="color" {...register('designBackgroundColor')} />
                  {errors.designBackgroundColor ? (
                    <span className="error-text">{errors.designBackgroundColor.message}</span>
                  ) : null}
                </label>

                <label className="form-field">
                  <span>Metin rengi</span>
                  <input type="color" {...register('designTextColor')} />
                  {errors.designTextColor ? (
                    <span className="error-text">{errors.designTextColor.message}</span>
                  ) : null}
                </label>
              </div>

              <div className="form-field">
                <span>Canlı önizleme</span>
                <div className="designer-preview-shell">
                  <div
                    className="ad-preview-card"
                    style={{
                      backgroundColor: designBackgroundColorValue || DEFAULT_BANNER_BACKGROUND_COLOR,
                      color: designTextColorValue || DEFAULT_BANNER_TEXT_COLOR,
                    }}
                  >
                    <div className="ad-preview-content">
                      <div className="ad-preview-title">
                        {titleValue || 'Başlık'}
                      </div>
                      <div className="ad-preview-subtitle">
                        {descriptionValue || 'Alt başlık'}
                      </div>
                    </div>
                    <span className="ad-preview-emoji">
                      {designEmojiValue || '📢'}
                    </span>
                  </div>
                </div>
                <span className="helper-text">
                  Mobil uygulamadaki görünümün birebir önizlemesi.
                </span>
              </div>
            </>
          )}

          <label className="form-field">
            <span>Hedef URL (opsiyonel)</span>
            <input
              type="url"
              placeholder="https://example.com/kampanya"
              {...register('targetUrl')}
            />
            {errors.targetUrl ? (
              <span className="error-text">{errors.targetUrl.message}</span>
            ) : null}
            <span className="helper-text">
              Banner&apos;a tıklandığında açılacak sayfa. Boş bırakılırsa tıklanabilir olmaz.
            </span>
          </label>

          <div className="form-row">
            <label className="form-field">
              <span>Sıra önceliği</span>
              <input type="number" min="0" {...register('orderPriority')} />
              <span className="helper-text">Yüksek değer = önce gösterilir</span>
            </label>
          </div>

          <div className="form-row">
            <label className="form-field">
              <span>Geçerlilik başlangıcı</span>
              <input type="date" {...register('validFrom')} />
            </label>
            <label className="form-field">
              <span>Geçerlilik bitişi</span>
              <input type="date" {...register('validUntil')} />
            </label>
          </div>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...register('isActive')} />
            <span>Banner aktif</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={isSubmitting || upsertMutation.isPending}
            >
              {editingBanner ? 'Bannerı kaydet' : 'Banner ekle'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
